import formatLocaleNumber from "~/helpers/localeNumberFormat";
import i18n from "~/i18n/i18n";

const SINGULAR = "Shipping UOM";
const PLURAL = "Shipping UOMs";
const UNSTATED_EACHES = "Not stated";

const tf = (key: string, options?: Record<string, unknown>): string => i18n.t(key, { ns: "fulfill", ...options });

const eachesPerUnitCompact = (eachesPerUnit: null | number): string =>
	eachesPerUnit == null ? UNSTATED_EACHES : `${eachesPerUnit} ea/unit`;

const shippingUomLabels = {
	add: `Add ${SINGULAR}`,
	get activate(): string {
		return tf("orders.shippingUom.activate");
	},
	get actionsColumnHeader(): string {
		return tf("orders.shippingUom.actionsColumnHeader");
	},
	get assignCategoryHelper(): string {
		return tf("orders.shippingUom.assignCategoryHelper");
	},
	assignDialogTitle: (containerCode: string): string =>
		tf("orders.shippingUom.assignDialogTitle", { code: containerCode }),
	get assignItemsBody(): string {
		return tf("orders.shippingUom.assignItemsBody");
	},
	assignItemsConfirm: (count: number): string =>
		count > 0
			? tf("orders.shippingUom.assignItemsConfirmCount", { count })
			: tf("orders.shippingUom.assignItemsConfirm"),
	get assignItemsFieldLabel(): string {
		return tf("orders.shippingUom.assignItemsFieldLabel");
	},
	get assignItemsNeedsSelection(): string {
		return tf("orders.shippingUom.assignItemsNeedsSelection");
	},
	get assignItemsPrimaryLabel(): string {
		return tf("orders.shippingUom.assignItemsPrimaryLabel");
	},
	get assignItemsRibbon(): string {
		return tf("orders.shippingUom.assignItemsRibbon");
	},
	get assignItemsSearchPlaceholder(): string {
		return tf("orders.shippingUom.assignItemsSearchPlaceholder");
	},
	assignItemsTo: (uomCode: string): string => tf("orders.shippingUom.assignItemsTo", { code: uomCode }),
	get assignToUnit(): string {
		return tf("orders.shippingUom.assignToUnit");
	},
	get belowOneUnit(): string {
		return tf("orders.shippingUom.belowOneUnit");
	},
	cappedLineCount: (count: number): string => tf("orders.shippingUom.cappedLineCount", { count }),
	changeDefaultDescription: `This will replace the existing default ${SINGULAR}. Continue?`,
	changeDefaultTitle: `Change default ${SINGULAR}?`,
	chipDefaultTag: "default",
	chipInheritedTag: "inherited",
	choiceCountOnItem: (total: number): string => tf("orders.shippingUom.choiceCountOnItem", { total }),
	get containerColumnHeader(): string {
		return tf("orders.shippingUom.containerColumnHeader");
	},
	get containerNameColumnHeader(): string {
		return tf("orders.shippingUom.containerNameColumnHeader");
	},
	containersOnUom: "Containers this unit ships on",
	countLabel: (count: number): string => `${count} ${count === 1 ? SINGULAR : PLURAL}`,
	csvCodelessCode: (code: string): string => tf("orders.shippingUom.csvCodelessCode", { code }),
	csvColumn: SINGULAR,
	csvInactiveCode: (code: string): string => `${SINGULAR} "${code}" is inactive`,
	csvSkipMessage: "Category already exists with the same Shipping UOM and will be skipped",
	csvUnknownCode: (code: string): string => `${SINGULAR} "${code}" was not found`,
	get currentCode(): string {
		return tf("orders.shippingUom.currentCode");
	},
	customerChangeAffectedLineCount: (count: number): string =>
		tf("orders.shippingUom.customerChangeAffectedLineCount", { count }),
	customerChangeKeepsPlacedLines: (items: string): string =>
		tf("orders.shippingUom.customerChangeKeepsPlacedLines", { items }),
	customerUnitNotOffered: (item: string): string => tf("orders.shippingUom.customerUnitNotOffered", { item }),
	deactivate: `Deactivate ${SINGULAR}`,
	deactivateDescription: (code: string): string => `This will deactivate ${SINGULAR} "${code}".`,
	deactivateTitle: `Deactivate ${SINGULAR}?`,
	defaultLabel: `Default ${SINGULAR}`,
	duplicateCode: `${SINGULAR} codes must be unique`,
	duplicateDescription: `${SINGULAR} descriptions must be unique`,
	eachesPerUnit: "Eaches Per Unit",
	eachesPerUnitCompact,
	get eachesPerUnitHelp(): string {
		return tf("orders.shippingUom.eachesPerUnitHelp");
	},
	get eachesPerUnitInvalid(): string {
		return tf("orders.shippingUom.eachesPerUnitInvalid");
	},
	eachesPerUnitSummary: (eachesPerUnit: null | number): string =>
		eachesPerUnit == null ? UNSTATED_EACHES : tf("orders.shippingUom.eachesPerUnitSummary", { eachesPerUnit }),
	eachesPerUnitUnstated: UNSTATED_EACHES,
	edit: `Edit ${SINGULAR}`,
	entity: SINGULAR,
	entityPlural: PLURAL,
	get frozenOnLine(): string {
		return tf("orders.shippingUom.frozenOnLine");
	},
	inheritedFromCategory:
		"Comes from a Category on this item. Change the Category to remove it, or add one here to override the default.",
	get inheritedFromItem(): string {
		return tf("orders.shippingUom.inheritedFromItem");
	},
	inheritedOnLegacyLineTooltip:
		"This line was placed before its Shipping UOM was recorded, so this is the item's. Open the line to see the Shipping UOM it routes on.",
	inheritedTooltip: (label: string): string => tf("orders.shippingUom.inheritedTooltip", { label }),
	itemAddContainerConfig: "Add Container Configuration",
	itemAddContainerConfigCategoryUnresolved: "Shipping UOM could not be resolved",
	itemAddContainerConfigContainerHelper: "The container this configuration belongs to",
	itemAddContainerConfigContainerRequired: "Container is required",
	get itemAddContainerConfigContainersLoading(): string {
		return tf("orders.shippingUom.itemAddContainerConfigContainersLoading");
	},
	get itemAddContainerConfigContainersUnavailable(): string {
		return tf("orders.shippingUom.itemAddContainerConfigContainersUnavailable");
	},
	itemAddContainerConfigDisabledReason: "Set a Shipping UOM on this item before adding a container configuration",
	itemAddContainerConfigLoadingReason: "Shipping UOM data is still loading. Try again in a moment.",
	itemAddContainerConfigNoContainers: "No containers found. Create one on the Containers page first.",
	itemAddContainerConfigReactivatesExisting: (container: string, uom: string): string =>
		tf("orders.shippingUom.itemAddContainerConfigReactivatesExisting", { container, uom }),
	itemAddContainerConfigUnavailableReason: "Shipping UOM data failed to load. Refresh the page and try again.",
	itemAddContainerConfigUomHelper: "Prefilled from this item's Shipping UOM",
	itemAddContainerConfigUpdatesExisting: (container: string, uom: string): string =>
		tf("orders.shippingUom.itemAddContainerConfigUpdatesExisting", { container, uom }),
	itemContainersCaption: `Every container this item ships on, based on its ${SINGULAR}. Edit Units Per Layer or Max Layers to override the inherited value for this item only — overridden cells are bold and tagged.`,
	get itemContainersCaptionCreateMode(): string {
		return tf("orders.shippingUom.itemContainersCaptionCreateMode");
	},
	itemNoContainers: `No containers found for this item's ${SINGULAR}. Assign one on the Details tab.`,
	itemOverrideHint: `This item overrides some inherited capacity. If several items need the same values, add them to a shared ${SINGULAR} instead of overriding each item one by one.`,
	itemsUsing: (uomCode: string): string => tf("orders.shippingUom.itemsUsing", { code: uomCode }),
	itemsUsingCaption: (count: number): string => tf("orders.shippingUom.itemsUsingCaption", { count }),
	itemsUsingNoRows: (uomCode: string): string => tf("orders.shippingUom.itemsUsingNoRows", { code: uomCode }),
	itemUomsWithoutContainers: (labels: string): string => tf("orders.shippingUom.itemUomsWithoutContainers", { labels }),
	namingHelper: `Name this the way your team talks about units — "10", "12", "4-inch".`,
	get noItemDefaultTooltip(): string {
		return tf("orders.shippingUom.noItemDefaultTooltip");
	},
	noneAssigned: `No ${PLURAL}`,
	noneAvailable: `No ${PLURAL} available`,
	noneFound: `No ${PLURAL} found.`,
	get noUnassignedContainerConfigs(): string {
		return tf("orders.shippingUom.noUnassignedContainerConfigs");
	},
	get notOnAnyUnit(): string {
		return tf("orders.shippingUom.notOnAnyUnit");
	},
	notOnAnyUnitCount: (count: number): string => tf("orders.shippingUom.notOnAnyUnitCount", { count }),
	notRecordedOnLine: "Not recorded",
	notRecordedOnLineTooltip:
		"Neither this line nor its item names a Shipping UOM. Open the line to see what it routes on.",
	get notSet(): string {
		return tf("orders.shippingUom.notSet");
	},
	onThisContainer: `${PLURAL} on this container`,
	get openContainersPage(): string {
		return tf("orders.shippingUom.openContainersPage");
	},
	get overridden(): string {
		return tf("orders.shippingUom.overridden");
	},
	overrideBelowContainerMinimum: (minimum: number): string =>
		tf("orders.shippingUom.overrideBelowContainerMinimum", { minimum }),
	overrideLabel: `Override ${SINGULAR}`,
	overrideNotAppliedBelowMinimum: (minimum: number): string =>
		tf("orders.shippingUom.overrideNotAppliedBelowMinimum", { minimum }),
	overrideNotAppliedNotMultiple: (increment: number): string =>
		tf("orders.shippingUom.overrideNotAppliedNotMultiple", { increment }),
	overrideNotContainerIncrementMultiple: (increment: number): string =>
		tf("orders.shippingUom.overrideNotContainerIncrementMultiple", { increment }),
	overrideNotOnItem: `This ${SINGULAR} is not assigned to the item`,
	get provenanceUnknownTooltip(): string {
		return tf("orders.shippingUom.provenanceUnknownTooltip");
	},
	get referenceListsLoading(): string {
		return tf("orders.shippingUom.referenceListsLoading");
	},
	get referenceListsUnavailable(): string {
		return tf("orders.shippingUom.referenceListsUnavailable");
	},
	removeDefaultDescription: `This is the default ${SINGULAR}. Removing it will make the first remaining ${SINGULAR} the new default.`,
	removeDefaultTitle: `Remove default ${SINGULAR}?`,
	required: `${SINGULAR} is required`,
	requiredDefault: `Exactly one ${SINGULAR} must be the default`,
	requiredWithOverride: `${SINGULAR} is required when Override is checked`,
	get mixedUnits(): string {
		return tf("orders.shippingUom.mixedUnits");
	},
	get retire(): string {
		return tf("orders.shippingUom.retire");
	},
	retireDescription: (containerCode: string): string =>
		tf("orders.shippingUom.retireDescription", { code: containerCode }),
	retireTitle: (containerCode: string): string => tf("orders.shippingUom.retireTitle", { code: containerCode }),
	roundedDownLineCount: (count: number): string => tf("orders.shippingUom.roundedDownLineCount", { count }),
	roundedDownToWholeUnits: (requested: string, adjusted: string): string =>
		tf("orders.shippingUom.roundedDownToWholeUnits", { adjusted, requested }),
	get routesOnTooltip(): string {
		return tf("orders.shippingUom.routesOnTooltip");
	},
	searchPlaceholder: `Search ${PLURAL}…`,
	get singleEachLabel(): string {
		return tf("orders.shippingUom.singleEachLabel");
	},
	get sizesLoading(): string {
		return tf("orders.shippingUom.sizesLoading");
	},
	stageStampDriftTooltip: (stampedUnits: number, eaches: number, eachesPerUnit: number, wholeUnits: number): string =>
		tf("orders.shippingUom.stageStampDriftTooltip", {
			eaches: formatLocaleNumber(eaches),
			eachesPerUnit,
			stampedUnits: formatLocaleNumber(stampedUnits),
			wholeUnits: formatLocaleNumber(wholeUnits)
		}),
	stampedOnLineTooltip: (label: string): string => tf("orders.shippingUom.stampedOnLineTooltip", { label }),
	stampedUnitsTooltip: (units: number, label: string): string =>
		tf("orders.shippingUom.stampedUnitsTooltip", { label, units: formatLocaleNumber(units) }),
	unitCellTooltip: (unitLabel: null | string | undefined, eachesPerUnit: null | number | undefined): string =>
		unitLabel && eachesPerUnit != null
			? tf("orders.shippingUom.unitCellTooltip", { eachesPerUnit, label: unitLabel })
			: "",
	unitCountWithEaches: (units: number, eaches: number, unitLabel?: null | string): string =>
		unitLabel
			? tf("orders.shippingUom.labelledUnitCountWithEaches", {
					eaches: formatLocaleNumber(eaches),
					label: unitLabel,
					units: formatLocaleNumber(units)
				})
			: tf("orders.shippingUom.unitCountWithEaches", {
					count: units,
					eaches: formatLocaleNumber(eaches),
					units: formatLocaleNumber(units)
				}),
	unitWithPack: (unit: { code: string; name: string }, eachesPerUnit: null | number | undefined): string =>
		`${unit.name || unit.code} · ${eachesPerUnitCompact(eachesPerUnit ?? null)}`,
	get unknown(): string {
		return tf("orders.shippingUom.unknown");
	},
	unorderableLineCount: (count: number): string => tf("orders.shippingUom.unorderableLineCount", { count }),
	get unresolved(): string {
		return tf("orders.shippingUom.unresolved");
	},
	unresolvedOnItems: (items: string): string => tf("orders.shippingUom.unresolvedOnItems", { items })
} as const;

export default shippingUomLabels;
